import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../context/AppContext';
import type { StudioLaunchStep } from '../../services/studioService';
import { StudioLaunchModal } from './StudioLaunchModal';
import { runStudioLaunchOnce } from './studioLaunchRunner';

interface Props {
  onClose: () => void;
}

/**
 * No-form Studio entry: guest session (if needed) → clone Support Agent → redirect with session handoff.
 */
export function StudioLaunchFlow({ onClose }: Props) {
  const { isLoggedIn, loading: sessionLoading, createGuestSession } = useApp();
  const needsGuest = !sessionLoading && !isLoggedIn;
  const [activeStep, setActiveStep] = useState<StudioLaunchStep>(needsGuest ? 'workspace' : 'open');
  const [error, setError] = useState<string | null>(null);

  const subtitle = needsGuest
    ? "No forms. We're setting up a guest workspace so you can start building right away."
    : "We're loading your Studio workspace so you can start building right away.";

  useEffect(() => {
    if (sessionLoading) return;

    let cancelled = false;

    void runStudioLaunchOnce({
      isLoggedIn,
      createGuestSession,
      onStep: (step) => {
        if (!cancelled) setActiveStep(step);
      },
    }).catch((err) => {
      if (!cancelled) {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      }
    });

    return () => {
      cancelled = true;
    };
    // Only sessionLoading — isLoggedIn flips after guest creation and must not restart this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLoading]);

  return createPortal(
    <StudioLaunchModal
      isOpen
      activeStep={activeStep}
      subtitle={subtitle}
      error={error}
      onDismissError={error ? onClose : undefined}
    />,
    document.body,
  );
}
